export type IncidentStatusClass =
  | 'investigating'
  | 'identified'
  | 'monitoring'
  | 'resolved';

export type MaintenanceStatusClass =
  | 'scheduled'
  | 'in-progress'
  | 'completed'
  | 'cancelled';

const INCIDENT_STATUS_CODE_MAP: Record<number, IncidentStatusClass> = {
  1: 'investigating',
  2: 'identified',
  3: 'monitoring',
  4: 'resolved'
};

const MAINTENANCE_STATUS_CODE_MAP: Record<number, MaintenanceStatusClass> = {
  1: 'scheduled',
  2: 'in-progress',
  3: 'completed',
  4: 'cancelled'
};

const INCIDENT_STATUS_LABELS: Record<IncidentStatusClass, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved'
};

const MAINTENANCE_STATUS_LABELS: Record<MaintenanceStatusClass, string> = {
  scheduled: 'Scheduled',
  'in-progress': 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

function parseStatusCode(status: unknown): number | null {
  if (typeof status === 'number' && Number.isFinite(status)) {
    return status;
  }

  if (typeof status !== 'string') {
    return null;
  }

  const trimmed = status.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }

  return Number(trimmed);
}

function normalizeStatusText(status: unknown): string {
  if (typeof status !== 'string') {
    return '';
  }

  return status
    .trim()
    .toLowerCase()
    .replaceAll(/[_-]+/g, ' ')
    .replaceAll(/\s+/g, ' ')
    .trim();
}

function formatStatusLabel(status: string): string {
  return status
    .replaceAll(/[_-]+/g, ' ')
    .replaceAll(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replaceAll(/\b\w/g, (char) => char.toUpperCase());
}

function detectIncidentStatusClass(status: unknown): IncidentStatusClass | null {
  const statusCode = parseStatusCode(status);
  if (statusCode !== null && INCIDENT_STATUS_CODE_MAP[statusCode]) {
    return INCIDENT_STATUS_CODE_MAP[statusCode];
  }

  const normalized = normalizeStatusText(status);
  if (!normalized) {
    return null;
  }

  if (normalized.includes('investigat')) {
    return 'investigating';
  }
  if (normalized.includes('identif')) {
    return 'identified';
  }
  if (normalized.includes('monitor')) {
    return 'monitoring';
  }
  if (
    normalized.includes('resolv') ||
    normalized.includes('clos') ||
    normalized.includes('complete') ||
    normalized.includes('done')
  ) {
    return 'resolved';
  }

  return null;
}

function detectMaintenanceStatusClass(status: unknown): MaintenanceStatusClass | null {
  const statusCode = parseStatusCode(status);
  if (statusCode !== null && MAINTENANCE_STATUS_CODE_MAP[statusCode]) {
    return MAINTENANCE_STATUS_CODE_MAP[statusCode];
  }

  const normalized = normalizeStatusText(status);
  if (!normalized) {
    return null;
  }

  if (normalized.includes('cancel') || normalized.includes('abort')) {
    return 'cancelled';
  }
  if (
    normalized.includes('in progress') ||
    normalized.includes('progress') ||
    normalized.includes('running') ||
    normalized.includes('active') ||
    normalized.includes('ongoing')
  ) {
    return 'in-progress';
  }
  if (
    normalized.includes('complete') ||
    normalized.includes('done') ||
    normalized.includes('finish') ||
    normalized.includes('resolv')
  ) {
    return 'completed';
  }
  if (
    normalized.includes('schedule') ||
    normalized.includes('planned') ||
    normalized.includes('upcoming')
  ) {
    return 'scheduled';
  }

  return null;
}

export function normalizeIncidentStatusClass(
  status: unknown,
  fallback: IncidentStatusClass = 'monitoring'
): IncidentStatusClass {
  return detectIncidentStatusClass(status) ?? fallback;
}

export function normalizeMaintenanceStatusClass(
  status: unknown,
  fallback: MaintenanceStatusClass = 'scheduled'
): MaintenanceStatusClass {
  return detectMaintenanceStatusClass(status) ?? fallback;
}

export function getIncidentStatusLabel(
  status: unknown,
  fallback: IncidentStatusClass = 'monitoring'
): string {
  const detected = detectIncidentStatusClass(status);
  if (detected) {
    return INCIDENT_STATUS_LABELS[detected];
  }

  if (typeof status === 'string' && status.trim()) {
    return formatStatusLabel(status);
  }

  return INCIDENT_STATUS_LABELS[fallback];
}

export function getMaintenanceStatusLabel(
  status: unknown,
  fallback: MaintenanceStatusClass = 'scheduled'
): string {
  const detected = detectMaintenanceStatusClass(status);
  if (detected) {
    return MAINTENANCE_STATUS_LABELS[detected];
  }

  if (typeof status === 'string' && status.trim()) {
    return formatStatusLabel(status);
  }

  return MAINTENANCE_STATUS_LABELS[fallback];
}
